import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { useDashboardData } from './useDashboardData';
import { NicheContentClient, ResolveNicheLabels, TenantClient } from '@shared/frontend';
import type { INicheLabels } from '@shared/frontend';

/**
 * Module-level cache of the last fully-resolved labels, keyed by tenant + language + labelsVersion.
 *
 * Every dashboard navigation re-mounts DashboardLayout, which re-runs this hook; because each `useDashboardData` call is an isolated fetch with no shared cache, the tenant/niche/profile reads
 * restart from `undefined` on every mount and the nav would flash the generic "Leads"/"Bookings" defaults before the niche nouns resolve again.
 *
 * Reusing the prior mount's resolved labels for the SAME tenant/language/version keeps navigation flicker-free while still re-resolving when something that legitimately changes the nouns bumps
 * the key (a business-profile save bumps labelsVersion; a language switch changes selectedLang).
 */
const labelCache = new Map<string, INicheLabels>();

/**
 * Resolves the niche-specific dashboard nouns for captured callers and appointments (e.g. "Patients" / "Appointments" for a clinic), falling back to the generic "Leads" / "Bookings" when neither
 * the tenant nor the niche defines an override. Niches flagged `allowCustomTerms` (the broad "Custom" catch-all) let the tenant name their own nouns in the business profile, so for those the
 * profile's custom labels take priority; the niche's own labels are held as the next fallback before the generic defaults. Niche labels are translated server-side with the rest of the catalog,
 * so they follow the active app language. The resolution logic itself lives in the shared `ResolveNicheLabels` helper so the Portal Tenant Details page resolves terminology identically.
 *
 * Resolved labels are memoised at module scope (see `labelCache`) so a page navigation renders the already-known nouns immediately instead of flashing the generic defaults while the underlying
 * reads re-fetch.
 *
 * @returns The resolved niche labels.
 */
export function useNicheLabels(): INicheLabels {
  const { tenantID, labelsVersion } = useDashboard();
  const { selectedLang } = useLocale();
  const tenant = useDashboardData((signal) => TenantClient.GetMyTenant(signal), []);
  const niches = useDashboardData((signal) => NicheContentClient.ListPublicNiches(signal, selectedLang), [selectedLang]);
  // `labelsVersion` is in the deps so a business-profile save (which bumps it via refreshLabels) re-resolves the tenant's custom Leads/Bookings nouns in every consumer, including the nav. The active
  // language is also a dep so the tenant's custom labels (authored in the tenant's own language) are re-fetched translated into the viewer's language via the profile `customLabelsDisplay` view.
  const profile = useDashboardData((signal) => TenantClient.GetProfile(signal, tenantID!, selectedLang), [tenantID, labelsVersion, selectedLang]);

  const cacheKey = `${tenantID ?? ''}:${selectedLang}:${labelsVersion}`;

  // Only trust a freshly-resolved value once all three reads have actually returned; a resolution off partial data would itself be a (wrong) flash. When ready, resolve and cache it.
  if (tenant.data && niches.data && profile.data) {
    const entry = niches.data.find((item) => item.niche === tenant.data?.niche);
    const resolved = ResolveNicheLabels(entry, profile.data.customLabelsDisplay ?? profile.data.customLabels, Localized.NavLeads, Localized.NavBookings);
    labelCache.set(cacheKey, resolved);
    return resolved;
  }

  // Reads still in flight (a remount on navigation): reuse the labels resolved on a prior mount for this exact tenant/language/version so the nav never flashes the generic defaults. Falls back to the
  // generic defaults only on the very first cold resolution, before any successful read has populated the cache.
  return labelCache.get(cacheKey) ?? ResolveNicheLabels(undefined, undefined, Localized.NavLeads, Localized.NavBookings);
}
